import { useState, useEffect } from "react";
import { Loader2, UserPlus, CheckCircle2, Send, Search } from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { useAuth } from "../../hooks/useAuth";
import { Button } from "../../components/ui";
import {
  fetchContacts,
  fetchMyAllowedSendAs,
  sendZoomInvitations,
} from "../../services/api";
import { getGraphToken } from "../../services/msal";
import { inputStyle } from "./constants";

export default function QuickInvite({ event, onInvited, existingEmails }) {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(null); // contactId being sent
  const [sent, setSent] = useState(new Set());
  const [error, setError] = useState(null);
  const [sendMethod, setSendMethod] = useState("o365");
  const [senderEmail, setSenderEmail] = useState("");
  const [allowedSendAs, setAllowedSendAs] = useState([]);
  const [loadingDelegates, setLoadingDelegates] = useState(true);

  // Load delegate accounts once
  useEffect(() => {
    fetchMyAllowedSendAs()
      .then((data) => {
        setAllowedSendAs(data.allowedEmails || []);
        if (data.allowedEmails?.length > 0)
          setSenderEmail(data.allowedEmails[0]);
      })
      .catch(() => setAllowedSendAs([]))
      .finally(() => setLoadingDelegates(false));
  }, []);

  // Debounced contact search
  useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      fetchContacts({ search: search.trim(), limit: 8 })
        .then((data) => {
          if (!cancelled) {
            // Filter out contacts already invited
            const filtered = (data.contacts || []).filter(
              (c) => c.email && !existingEmails.has(c.email.toLowerCase()),
            );
            setResults(filtered);
          }
        })
        .catch(() => {})
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, existingEmails]);

  const handleSend = async (contact) => {
    setSending(contact.id);
    setError(null);
    try {
      let graphToken = null;
      if (sendMethod === "o365") graphToken = await getGraphToken();

      await sendZoomInvitations(event.id, {
        contacts: [
          {
            contactId: contact.id,
            email: contact.email,
            firstName: contact.firstName,
            lastName: contact.lastName,
            company: contact.company,
            tags: contact.tags || [],
          },
        ],
        sendMethod,
        senderEmail: sendMethod === "o365" ? senderEmail : undefined,
        graphToken,
      });
      setSent((prev) => new Set(prev).add(contact.id));
      onInvited();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(null);
    }
  };

  return (
    <div
      style={{
        marginBottom: 16,
        borderBottom: `1px solid ${colors.border}`,
        paddingBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 8,
        }}
      >
        <UserPlus size={13} color={colors.accent} />
        <span style={{ fontSize: 12, fontWeight: 600, color: colors.text }}>
          Quick Invite
        </span>
      </div>

      {/* Send method toggle */}
      <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
        {[
          { id: "o365", label: "Personal O365" },
          { id: "sendgrid", label: "SendGrid" },
        ].map((m) => (
          <button
            key={m.id}
            onClick={() => setSendMethod(m.id)}
            style={{
              padding: "3px 8px",
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 600,
              fontFamily: fonts.body,
              border: `1px solid ${sendMethod === m.id ? colors.accent : colors.border}`,
              background:
                sendMethod === m.id ? colors.accentDim : "transparent",
              color: sendMethod === m.id ? colors.accent : colors.textSecondary,
              cursor: "pointer",
            }}
          >
            {m.label}
          </button>
        ))}
      </div>

      {/* Sender dropdown for O365 */}
      {sendMethod === "o365" &&
        !loadingDelegates &&
        allowedSendAs.length > 1 && (
          <select
            value={senderEmail}
            onChange={(e) => setSenderEmail(e.target.value)}
            style={{
              ...inputStyle,
              fontSize: 11,
              padding: "5px 8px",
              marginBottom: 8,
              cursor: "pointer",
              appearance: "auto",
            }}
          >
            {allowedSendAs.map((email) => (
              <option key={email} value={email}>
                {email}
                {email === user?.userDetails?.toLowerCase() ? " (you)" : ""}
              </option>
            ))}
          </select>
        )}

      {/* Search box */}
      <div style={{ position: "relative" }}>
        <Search
          size={13}
          color={colors.textSecondary}
          style={{ position: "absolute", left: 10, top: 10 }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search contacts to invite..."
          style={{ ...inputStyle, paddingLeft: 30, fontSize: 12 }}
        />
      </div>

      {error && (
        <div style={{ fontSize: 11, color: colors.danger, marginTop: 6 }}>
          {error}
        </div>
      )}

      {/* Results */}
      {search.trim() && (
        <div
          style={{
            marginTop: 6,
            maxHeight: 200,
            overflowY: "auto",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
          }}
        >
          {loading ? (
            <div
              style={{
                padding: 12,
                textAlign: "center",
                color: colors.textSecondary,
              }}
            >
              <Loader2
                size={14}
                className="spin"
              />
            </div>
          ) : results.length === 0 ? (
            <div
              style={{
                padding: 12,
                textAlign: "center",
                color: colors.textSecondary,
                fontSize: 12,
              }}
            >
              {search.trim() ? "No matching contacts (or already invited)" : ""}
            </div>
          ) : (
            results.map((c) => (
              <div
                key={c.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 10px",
                  borderBottom: `1px solid ${colors.borderLight}`,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: colors.text,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {[c.firstName, c.lastName].filter(Boolean).join(" ") ||
                      c.email}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: colors.textSecondary,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.email}
                    {c.company ? ` — ${c.company}` : ""}
                  </div>
                </div>
                {sent.has(c.id) ? (
                  <CheckCircle2 size={14} color={colors.success} />
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleSend(c)}
                    style={{
                      padding: "3px 10px",
                      fontSize: 11,
                      minWidth: 0,
                      opacity: sending ? 0.5 : 1,
                    }}
                    disabled={!!sending}
                  >
                    {sending === c.id ? (
                      <Loader2
                        size={12}
                        className="spin"
                      />
                    ) : (
                      <>
                        <Send size={11} /> Invite
                      </>
                    )}
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
